import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Icon } from './Icon';
import { applyTheme, getTheme, type ThemePref } from '../lib/theme';

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div class="row muted" role="status" aria-live="polite" style="padding:24px 0"><span class="spinner" />{label}</div>;
}

export function ErrorBox({ message, onRetry, children }: { message: string; onRetry?: () => void; children?: ComponentChildren }) {
  return (
    <div class="alert error" role="alert">
      <span class="alert-icon"><Icon name="alert" /></span>
      <div class="grow"><p>{message}</p>{children}</div>
      {onRetry && <button class="btn sm" type="button" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'success' | 'warning' | 'error'; children: ComponentChildren }) {
  const icon = tone === 'success' ? 'check' : tone === 'info' ? 'info' : 'alert';
  return (
    <div class={`alert ${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span class="alert-icon"><Icon name={icon} /></span>
      <div class="grow">{children}</div>
    </div>
  );
}

export function Empty({ icon = 'folder', text, action }: { icon?: string; text: string; action?: ComponentChildren }) {
  return <div class="empty"><Icon name={icon} size={36} /><p>{text}</p>{action}</div>;
}

export function ProgressBar({ value, label }: { value: number | null; label: string }) {
  const pct = value === null ? null : Math.max(0, Math.min(100, value * 100));
  return (
    <div class={`progress${pct === null ? ' indeterminate' : ''}`} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct === null ? undefined : Math.round(pct)}>
      <span style={{ width: `${pct ?? 30}%` }} />
    </div>
  );
}

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    // Re-sync: the connection may have changed between the first render and subscribing.
    setOnline(navigator.onLine);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return online;
}

export function OfflineBar() {
  const online = useOnline();
  if (online) return null;
  return <div class="offline-bar" role="status">You’re offline. Changes and downloads will resume when you reconnect.</div>;
}

export function ThemeToggle({ withLabel = false }: { withLabel?: boolean }) {
  const [pref, setPref] = useState<ThemePref>(getTheme());
  useEffect(() => {
    const on = () => setPref(document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark');
    window.addEventListener('ayx:theme', on);
    return () => window.removeEventListener('ayx:theme', on);
  }, []);
  const next: ThemePref = pref === 'dark' ? 'light' : 'dark';
  return (
    <button type="button" class={withLabel ? 'btn ghost sm' : 'icon-btn'} data-testid="theme-toggle" aria-label={`Theme: ${pref}. Switch to ${next}.`} title={`Switch to ${next} theme`}
      onClick={() => { applyTheme(next); setPref(next); }}>
      <Icon name={pref === 'light' ? 'sun' : 'moon'} />
      {withLabel && <span>{pref === 'dark' ? 'Dark theme' : 'Light theme'}</span>}
    </button>
  );
}

export function UpgradeCard({ title = 'Available on paid plans', text, compact = false }: { title?: string; text: string; compact?: boolean }) {
  return (
    <div class="upgrade-card" data-testid="upgrade-card">
      <span class="ficon"><Icon name="sparkles" size={20} /></span>
      <div class="stack grow" style="gap:4px">
        <h2 style={compact ? 'font-size:17px;margin:0' : 'margin:0;font-size:19px'}>{title}</h2>
        <p>{text}</p>
      </div>
      <a class="btn primary" href="/pricing">See plans</a>
    </div>
  );
}

export function SkeletonCards({ n = 4, label = 'Loading videos' }: { n?: number; label?: string }) {
  return (
    <div class="video-row" role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: n }, (_, i) => <div key={i} class="video-card sk"><div class="thumb skeleton" /><div class="skeleton" style="height:14px;width:80%" /><div class="skeleton" style="height:12px;width:55%" /></div>)}
    </div>
  );
}

/** Small helper so long-running async buttons show their state and avoid double submits. */
export function useBusy() {
  const [busy, setBusy] = useState<string | null>(null);
  const run = async <T,>(key: string, fn: () => Promise<T>): Promise<T | undefined> => {
    if (busy) return undefined;
    setBusy(key);
    try { return await fn(); } finally { setBusy(null); }
  };
  return { busy, run };
}

// ---- Toasts: short confirmations for actions taken from menus ----
type ToastMsg = { id: number; text: string; tone: 'success' | 'error' | 'info' };
let toastSeq = 0;
export function toast(text: string, tone: ToastMsg['tone'] = 'success') {
  window.dispatchEvent(new CustomEvent<ToastMsg>('ayx:toast', { detail: { id: ++toastSeq, text, tone } }));
}
export function Toaster() {
  const [list, setList] = useState<ToastMsg[]>([]);
  useEffect(() => {
    const on = (e: Event) => {
      const t = (e as CustomEvent<ToastMsg>).detail;
      setList((l) => [...l.slice(-2), t]);
      setTimeout(() => setList((l) => l.filter((x) => x.id !== t.id)), 4500);
    };
    window.addEventListener('ayx:toast', on);
    return () => window.removeEventListener('ayx:toast', on);
  }, []);
  return (
    <div class="toaster" role="status" aria-live="polite">
      {list.map((t) => <div key={t.id} class={`toast ${t.tone}`}><Icon name={t.tone === 'error' ? 'alert' : t.tone === 'success' ? 'checkCircle' : 'info'} size={18} />{t.text}</div>)}
    </div>
  );
}
